/**
 * Utilidades para formateo consistente de fechas y números en la UI.
 *
 * Política:
 *  1. Si el usuario seteó un locale preferido en localStorage (`boveda.locale`),
 *     se usa ese (a futuro: setting visible en su perfil).
 *  2. Si no, se usa el locale del navegador/SO (`navigator.language`) cuando
 *     declara una variante latinoamericana o europea (`es-*`, `pt-*`, `it-*`,
 *     `fr-*`, `de-*` — todos formatean dd/mm/yyyy o similar).
 *  3. Fallback: `es-AR` (formato latinoamericano dd/mm/yyyy).
 *
 * Esto evita el bug histórico donde `toLocaleString()` sin parámetros tomaba
 * `en-US` en macOS en inglés → mm/dd/yyyy mezclado con dd/mm/yyyy en otras
 * pantallas.
 */

const FALLBACK_LOCALE = 'es-AR';

/**
 * Locales que formatean fechas como dd/mm/yyyy o similar (no americanos).
 * Si el navegador declara uno de estos, lo respetamos. Si declara `en-US` u
 * otro americano, caemos al fallback `es-AR`.
 */
const ACCEPTED_LOCALE_PREFIXES = ['es-', 'pt-', 'it-', 'fr-', 'de-', 'ca-', 'eu-', 'gl-'];

/**
 * Devuelve el locale preferido para formateo. Lee primero del localStorage
 * (override explícito del usuario) y después del navegador. Si nada matchea,
 * cae a {@link FALLBACK_LOCALE}.
 */
export function getUserLocale() {
  // 1. Override explícito del usuario (a futuro: pantalla de preferencias).
  try {
    const stored = typeof window !== 'undefined' && window.localStorage?.getItem('boveda.locale');
    if (stored && stored.trim()) return stored.trim();
  } catch {
    // localStorage puede tirar en modo privado / sandbox; ignorar.
  }

  // 2. Locale del navegador, si no es americano.
  if (typeof navigator !== 'undefined' && navigator.language) {
    const browserLocale = navigator.language;
    if (ACCEPTED_LOCALE_PREFIXES.some((pfx) => browserLocale.startsWith(pfx))) {
      return browserLocale;
    }
  }

  // 3. Fallback latinoamericano.
  return FALLBACK_LOCALE;
}

/**
 * Formatea una fecha (Date | ISO string | epoch ms) como dd/mm/yyyy según el
 * locale del usuario. Tolera valores nulos / inválidos devolviendo el placeholder.
 */
export function formatDate(value, { placeholder = '-' } = {}) {
  if (value === null || value === undefined || value === '') return placeholder;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return placeholder;
  return d.toLocaleDateString(getUserLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Formatea una fecha + hora (Date | ISO string | epoch ms) como dd/mm/yyyy HH:mm:ss
 * según el locale del usuario.
 */
export function formatDateTime(value, { placeholder = '-', withSeconds = true } = {}) {
  if (value === null || value === undefined || value === '') return placeholder;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return placeholder;
  return d.toLocaleString(getUserLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
    hour12: false,
  });
}

/**
 * Formatea solo la hora (HH:mm:ss) según el locale del usuario.
 */
export function formatTime(value, { placeholder = '-', withSeconds = true } = {}) {
  if (value === null || value === undefined || value === '') return placeholder;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return placeholder;
  return d.toLocaleTimeString(getUserLocale(), {
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
    hour12: false,
  });
}

/**
 * Formatea un número con separadores de miles según el locale del usuario.
 * Usado para conteos y volúmenes (ej. "1.234.567 documentos").
 */
export function formatNumber(value, { placeholder = '-' } = {}) {
  if (value === null || value === undefined || value === '') return placeholder;
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return placeholder;
  return n.toLocaleString(getUserLocale());
}
